import { Link } from 'react-router-dom';
import type { AssistantResponse, MeetingListResponse, OverviewResponse, TodoListResponse } from '../../shared/api-types.js';
import { autoProcessWindowLabel } from '../../shared/fellow.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { RegionErrorBoundary } from '../shell/RegionErrorBoundary.js';
import {
  Banner,
  Button,
  Chip,
  FailureView,
  LinkButton,
  Page,
  PageHead,
  ProjectChip,
  RelativeTime,
  Section,
  StatTile,
  StatTileRow,
  StateView,
  StatusPill,
  browserTimeZone,
  formatCount,
  formatDate,
  formatTime,
  pluralise,
  useRequest,
  type RequestState,
} from '../ui/index.js';
import { executiveOffered } from '../shell/executive-badge.js';
import { ExecutiveCard } from './executive-card.js';
import { briefCoverageText, briefPlainText } from './lib/assistant.js';
import { overviewNotice } from './lib/connection.js';
import { overviewLinkLine, overviewPath, todoWithoutMeetingLabel } from './lib/todos.js';
import { summaryPresentation } from './lib/summary.js';
import { DueDate, PriorityPill, useToday } from './todo-table.js';
import './pages.css';

// The overview (spec §13): stat tiles from /api/overview, the next open to-dos by due date, the
// most recent meetings — with a notice above them when older meetings were imported but await
// processing (spec §10) — and a connection notice that keeps "not connected", "failing", "not
// switched on" and "not permitted" apart (app-baseline 05-client §5.4). Each region loads on its
// own so a slow list never blocks the tiles (03-components §1).

const NEXT_UP_LIMIT = 5;
const RECENT_LIMIT = 5;

export default function OverviewPage() {
  const me = useMe();
  // The browser's zone with each request (the owner's ruling Q13): "overdue" and "due this week" cut at the reader's midnight.
  const overview = useRequest<OverviewResponse>((signal) => api.get<OverviewResponse>(overviewPath(browserTimeZone()), { signal }), []);
  const nextUp = useRequest<TodoListResponse>((signal) => api.get<TodoListResponse>('/api/todos?status=open&sort=due_date&dir=asc', { signal }), []);
  const recent = useRequest<MeetingListResponse>((signal) => api.get<MeetingListResponse>('/api/meetings?sort=started_at&dir=desc', { signal }), []);

  const notice = overview.data ? overviewNotice(overview.data.connection, me, overview.data.connectionErrorCode) : null;
  const tilesLoading = overview.status === 'loading';
  const data = overview.data;

  // Nothing imported and nothing to do: guide the reader to the one thing that fills the page.
  const nothingYet = data !== null && data.meetingsImported === 0 && nextUp.data !== null && nextUp.data.total === 0;

  return (
    <Page measure="wide">
      <PageHead title="Overview" />

      {overview.status === 'error' && overview.error ? (
        <FailureView failure={overview.error} what="your overview" onRetry={overview.reload} minHeight="12rem" />
      ) : (
        <StatTileRow minWidth="8.5rem">
          <StatTile label="Open to-dos" value={data?.openTodos ?? null} loading={tilesLoading} to={PATHS.todos} />
          <StatTile label="Due this week" value={data?.dueThisWeek ?? null} loading={tilesLoading} to={`${PATHS.todos}?sort=due_date&dir=asc`} hint="Today and the next six days" valueTone={data && data.dueThisWeek > 0 ? 'warning' : 'default'} />
          <StatTile label="Overdue" value={data?.overdue ?? null} loading={tilesLoading} to={`${PATHS.todos}?sort=due_date&dir=asc`} valueTone={data && data.overdue > 0 ? 'danger' : 'default'} />
          <StatTile label="Meetings imported" value={data?.meetingsImported ?? null} loading={tilesLoading} to={PATHS.meetings} />
        </StatTileRow>
      )}

      {notice ? (
        <Banner
          tone={notice.tone}
          title={notice.title}
          icon={notice.kind === 'not-configured' || notice.kind === 'revoked' ? 'unplug' : notice.kind === 'not-permitted' ? 'lock' : notice.kind === 'error' ? 'alert-triangle' : 'plug'}
          actions={notice.actionLabel ? <LinkButton to={PATHS.connections} variant={notice.kind === 'error' || notice.kind === 'revoked' ? 'secondary' : 'primary'} size="sm">{notice.actionLabel}</LinkButton> : undefined}
        >
          {notice.text}
        </Banner>
      ) : data && data.connection === 'connected' ? (
        <p className="overview-status">
          <StatusPill tone="success">Fellow connected</StatusPill>
          <span>
            Last sync {data.lastSyncAt ? <RelativeTime value={data.lastSyncAt} /> : 'has not completed yet'}
            {' · '}
            <Link to={PATHS.connections}>Connections</Link>
          </span>
        </p>
      ) : null}

      {/* Why above the person's own regions and whatever they hold: the executives' daily door (ADR 0019; design §2.13),
          which reads nothing of this person's own meetings, so an executive with none still meets it. */}
      {executiveOffered(me) ? (
        <RegionErrorBoundary region="Executive intelligence" compact>
          <ExecutiveCard />
        </RegionErrorBoundary>
      ) : null}

      {nothingYet ? (
        <StateView
          kind="empty"
          icon="calendar"
          title={data.connection === 'connected' ? 'Your meetings are on their way' : 'Nothing here yet'}
          description={data.connection === 'connected'
            ? 'The first import usually finishes within a minute or two of connecting.'
            // Why so short: the banner above already says what Fellow imports and why connecting is or is
            // not possible; this line only names what fills the page, and stays true under every banner.
            : 'Meetings and to-dos appear here once Fellow is connected.'}
          action={data.connection === 'connected'
            ? <Button variant="secondary" icon="refresh" onClick={() => { overview.reload(); nextUp.reload(); recent.reload(); }}>Check again</Button>
            : data.connection === 'not_connected' && me.capabilities.fellowConnect
              // Why secondary: the notice above already carries this page's one primary action.
              ? <LinkButton to={PATHS.connections} variant="secondary" icon="plug">Connect Fellow</LinkButton>
              : undefined}
          minHeight="16rem"
        />
      ) : (
        <>
          {me.capabilities.aiSummaries && me.integrations.assistant !== 'off' ? (
            <RegionErrorBoundary region="General Intelligence" compact>
              <GeneralIntelligenceCard />
            </RegionErrorBoundary>
          ) : null}
        <div className="overview-grid">
          <RegionErrorBoundary region="the next to-dos" compact>
            <Section
              id="next-up"
              title="Next up"
              description={nextUp.data ? extentLine(Math.min(nextUp.data.items.length, NEXT_UP_LIMIT), nextUp.data.matching, 'open to-do', 'open to-dos', 'soonest due first') : undefined}
              actions={<LinkButton to={PATHS.todos} variant="link" size="sm" trailingIcon="chevron-right">All to-dos</LinkButton>}
            >
              <LinkChangesLine request={nextUp} />
              <NextUpList request={nextUp} />
            </Section>
          </RegionErrorBoundary>
          <RegionErrorBoundary region="recent meetings" compact>
            <Section
              id="recent-meetings"
              title="Recent meetings"
              description={recent.data ? extentLine(Math.min(recent.data.items.length, RECENT_LIMIT), recent.data.total, 'meeting', 'meetings', 'most recent first') : undefined}
              actions={<LinkButton to={PATHS.meetings} variant="link" size="sm" trailingIcon="chevron-right">All meetings</LinkButton>}
            >
              {data && data.awaitingProcessing > 0 ? (
                // Directly above the list it concerns (03-components §6.2): the meetings the importer
                // stored outside the automatic window and left for the reader to process.
                <Banner
                  tone="info"
                  icon="clock"
                  actions={<LinkButton to={`${PATHS.meetings}?processed=false`} variant="secondary" size="sm">Review them</LinkButton>}
                >
                  {pluralise(data.awaitingProcessing, {
                    one: `# meeting older than ${autoProcessWindowLabel()} was imported but not processed.`,
                    other: `# meetings older than ${autoProcessWindowLabel()} were imported but not processed.`,
                  })}
                </Banner>
              ) : null}
              <RecentMeetingsList request={recent} />
            </Section>
          </RegionErrorBoundary>
        </div>
        </>
      )}
    </Page>
  );
}

/**
 * The compact General Intelligence card (owner ruling A9: Overview links to the Assistant): the brief's
 * headline and the way to the page, for a person who holds AI summaries. Its own request, so a slow or
 * refused brief never holds the tiles; a refusal renders the state the server names.
 */
function GeneralIntelligenceCard() {
  const brief = useRequest<AssistantResponse>((signal) => api.get<AssistantResponse>('/api/assistant', { signal }), []);
  const data = brief.data;
  const body = (() => {
    if (brief.status === 'loading' && !data) return <StateView kind="loading" label="Loading your brief" minHeight="6rem" compact className="item-list__state" />;
    if (brief.status === 'error' && brief.error && !data) return <FailureView failure={brief.error} what="your brief" featureName="the assistant" onRetry={brief.reload} compact />;
    if (!data) return null;
    if (data.state === 'ready' && data.brief) {
      return (
        <div className="stack stack--2">
          <p className="assistant-headline">{briefPlainText(data.brief.payload.headline)}</p>
          <p className="summary-provenance">
            {data.brief.isDemo ? <Chip tone="warning" icon="bolt" title="Written by the demo assistant, not by an AI model">Demo</Chip> : null}
            <span>Updated <RelativeTime value={data.brief.updatedAt} /> · {briefCoverageText(data.brief)}</span>
          </p>
        </div>
      );
    }
    const text = data.state === 'unreadable'
      ? 'Your latest brief can no longer be opened; the next one replaces it.'
      : data.state === 'withheld'
        ? 'Your latest brief is set aside; the next one is written without Slack findings.'
        : data.job
          ? 'Your first brief is on its way.'
          : `No brief yet. Calis writes one from your meetings of the last ${formatCount(data.windowDays)} days once their summaries and Slack checks arrive.`;
    return <p className="secondary">{text}</p>;
  })();
  return (
    <Section
      id="general-intelligence"
      title="General Intelligence"
      actions={<LinkButton to={PATHS.assistant} variant="link" size="sm" trailingIcon="chevron-right">Open the Assistant</LinkButton>}
    >
      <div className="assistant-headline-block">{body}</div>
    </Section>
  );
}

/**
 * Overview's one line about changes that reached linked to-dos (design §7, F6): "2 to-dos ticked off by
 * colleagues", "1 to-do marked done in Fellow", which opens the To-dos page's "Changed through a link" review. Read from the same list request as Next up,
 * whose counts cover every unseen change whatever its status. Nothing while there is none.
 */
function LinkChangesLine({ request }: { request: RequestState<TodoListResponse> }) {
  const line = overviewLinkLine(request.data?.linkChanges);
  if (!line) return null;
  return (
    <Banner
      tone="info"
      icon="link"
      actions={<LinkButton to={`${PATHS.todos}?status=all&changed=1`} variant="secondary" size="sm">Review<span className="visually-hidden"> the changed to-dos</span></LinkButton>}
    >
      {line}
    </Banner>
  );
}

/**
 * "5 most recent of 42 meetings" — a subset never presents as the whole (06-modules §5.3 R3). Nothing
 * at zero: the empty state beneath owns that case, and a head saying it too would state it a third time.
 */
function extentLine(shown: number, total: number, one: string, other: string, rule: string): string | undefined {
  if (total === 0) return undefined;
  if (shown >= total) return pluralise(total, { one: `# ${one}`, other: `# ${other}` });
  return `${formatCount(shown)} of ${pluralise(total, { one: `# ${one}`, other: `# ${other}` })}, ${rule}`;
}

function NextUpList({ request }: { request: RequestState<TodoListResponse> }) {
  const today = useToday();
  if (request.status === 'loading') return <StateView kind="loading" label="Loading your next to-dos" minHeight="12rem" className="item-list__state" />;
  if (request.status === 'error' && request.error) return <FailureView failure={request.error} what="your next to-dos" onRetry={request.reload} compact />;
  const items = (request.data?.items ?? []).slice(0, NEXT_UP_LIMIT);
  if (items.length === 0) {
    return (
      <StateView
        kind="empty"
        icon="check-square"
        title="Nothing open"
        description="Open to-dos from your meetings appear here, soonest due first."
        action={<LinkButton to={PATHS.todos} variant="secondary" size="sm">Open the to-do list</LinkButton>}
        compact
        className="item-list__state"
      />
    );
  }
  return (
    <ol className="item-list" role="list" aria-label="Next open to-dos, soonest due first">
      {items.map((todo) => (
        <li key={todo.id}>
          <div className="item-list__main">
            <Link to={`${PATHS.todos}?highlight=${encodeURIComponent(todo.id)}`} className="item-list__title hit-area">{todo.text}</Link>
            <div className="item-list__meta">
              {todo.meeting ? <Link to={PATHS.meeting(todo.meeting.id)} className="item-list__meta-link">{todo.meeting.title}</Link> : <span>{todoWithoutMeetingLabel(todo)}</span>}
              {/* The first project only (design §7.7): one chip keeps the row a row; the rest are on the to-do itself. Under PROJECTS_MODE=off the server sends none. */}
              {todo.projects[0] ? <ProjectChip project={todo.projects[0]} /> : null}
              {todo.responsible ? <span>{todo.responsible}</span> : null}
            </div>
          </div>
          <div className="item-list__aside">
            <PriorityPill priority={todo.priority} />
            <DueDate dueDate={todo.dueDate} status={todo.status} today={today} />
          </div>
        </li>
      ))}
    </ol>
  );
}

function RecentMeetingsList({ request }: { request: RequestState<MeetingListResponse> }) {
  if (request.status === 'loading') return <StateView kind="loading" label="Loading recent meetings" minHeight="12rem" className="item-list__state" />;
  if (request.status === 'error' && request.error) return <FailureView failure={request.error} what="your recent meetings" onRetry={request.reload} compact />;
  const items = (request.data?.items ?? []).slice(0, RECENT_LIMIT);
  if (items.length === 0) {
    return (
      <StateView
        kind="empty"
        icon="calendar"
        title="No meetings imported yet"
        // Why state-neutral: this renders under "Fellow connected" too, while the first import is pending.
        description="Meetings appear here once they are imported from Fellow."
        action={<LinkButton to={PATHS.connections} variant="secondary" size="sm">Open Connections</LinkButton>}
        compact
        className="item-list__state"
      />
    );
  }
  return (
    <ol className="item-list" role="list" aria-label="Most recent meetings">
      {items.map((meeting) => {
        const state = summaryPresentation(meeting.summaryStatus, meeting.transcriptStatus, meeting.processedAt);
        return (
          <li key={meeting.id}>
            <div className="item-list__main">
              <Link to={PATHS.meeting(meeting.id)} className="item-list__title hit-area">{meeting.title}</Link>
              <div className="item-list__meta">
                <span>{meeting.startedAt ? `${formatDate(meeting.startedAt)}, ${formatTime(meeting.startedAt)}` : 'Date not recorded'}</span>
                <span className="num">{pluralise(meeting.openTodoCount, { one: '# open to-do', other: '# open to-dos' })}</span>
              </div>
            </div>
            <div className="item-list__aside">
              <StatusPill tone={state.tone} icon={state.icon} title={state.detail}>{state.word}</StatusPill>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
